import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { useAccessContext } from '../../../context/AccessContext'
import { useI18n } from '../../../i18n/I18nContext'
import { MfaEnrollment } from '../../auth/MfaEnrollment'
import type { MfaEnrollmentDetails } from '../../../services/auth'
import { workerRequestFailure } from '../../../services/workerFailure'
import type { WorkerDataKey, WorkerWorkspaceState } from './useWorkerWorkspaceData'

// Group dependencies cover every data section rendered by each shell destination.
const sections: Record<string, WorkerDataKey[]> = {
  today: ['today', 'upcoming', 'history', 'punch', 'shifts', 'locationPrivacy'],
  home: ['today', 'upcoming', 'punch', 'locationPrivacy'],
  'find-work': ['shifts', 'recommended', 'applications'],
  schedule: ['today', 'upcoming', 'history'], shifts: ['today', 'upcoming', 'history', 'shifts', 'recommended', 'applications'],
  availability: ['availability', 'unavailability'], timesheets: ['timesheets'], time: ['timesheets', 'punch', 'history'],
  messages: ['threads'], profile: ['profile'], documents: ['documents'], skills: ['skills', 'progression'],
  notifications: ['notifications'], decisions: ['decisions', 'appeals'],
}
export function workerSectionUnavailable(state: WorkerWorkspaceState, destination: string) {
  return Boolean((sections[destination] || []).some(key => state.failures[key])
    || ((state.authenticationIssue || state.recoveryCodesPending) && !['profile', 'permissions', 'more', 'learning', 'mentorship'].includes(destination)))
}
export function WorkerSectionUnavailable({ retry }: { retry: () => Promise<void> }) {
  const { t } = useI18n()
  return <section className="worker-card" data-testid="worker-section-unavailable">
    <h2>{t('This section is unavailable')}</h2>
    <p>{t('Complete the required authentication or retry to load current information.')}</p>
    <button className="btn btn-secondary" onClick={() => void retry()}>{t('Retry')}</button>
  </section>
}

/** Owner enrollment uses the canonical source-session endpoints. No destination tenant is supplied. */
export function WorkerRequestRecovery({ state }: { state: WorkerWorkspaceState }) {
  const auth = useAuth()
  const { selectedTenantId, accessContext } = useAccessContext()
  const { t } = useI18n()
  const [open, setOpen] = useState(() => auth.hasPendingMfaRecoveryCodes())
  const [enrollment, setEnrollment] = useState<MfaEnrollmentDetails | null>(null)
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[]>(() => auth.getPendingMfaRecoveryCodes())
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [expired, setExpired] = useState(false)
  // Confirmation may finish while switching phone/wide shells. The canonical
  // provider keeps the one-time completion in memory until acknowledgment.
  useEffect(() => {
    const pending = auth.getPendingMfaRecoveryCodes()
    if (pending.length && !codes.length) {
      setCodes(pending); setOpen(true); setEnrollment(null); setCode('')
    }
  }, [auth, codes.length])
  const inFlight = useRef(false)
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const clear = () => { setEnrollment(null); setCode(''); setError('') }
  const perform = async (action: () => Promise<void>) => {
    if (inFlight.current) return
    inFlight.current = true; setBusy(true); setError('')
    try { await action() }
    catch (failure) { if (mounted.current) setError(workerRequestFailure(failure).message) }
    finally { inFlight.current = false; if (mounted.current) setBusy(false) }
  }
  const begin = () => void perform(async () => {
    const result = await auth.startMfaEnrollment()
    if (mounted.current) { setEnrollment(result.enrollment); setExpired(false) }
  })
  const confirm = (event: React.FormEvent) => {
    event.preventDefault()
    void perform(async () => {
      const result = await auth.confirmMfaEnrollment(code)
      if (!mounted.current) return
      clear(); setCodes(result.recoveryCodes || [])
      if (!result.recoveryCodes?.length) throw new Error('Recovery codes were not returned. Sign in again before continuing.')
    })
  }
  const acknowledge = () => void perform(async () => {
    await auth.acknowledgeMfaRecoveryCodes()
    if (!mounted.current) return
    setCodes([]); setOpen(false)
    await state.load()
  })
  const signin = () => void perform(async () => {
    // Persist only navigation context. Challenge tokens, setup material and codes remain in memory.
    const next = `${window.location.pathname}${window.location.search}`
    const params = new URLSearchParams({ next })
    const tenant = selectedTenantId || accessContext?.tenantId
    if (tenant) params.set('tenantId', tenant)
    await auth.logout()
    window.location.assign(`/login?${params}`)
  })
  return <>
    {state.error && <p className="worker-inline-error" role="alert">{t(state.error)}</p>}
    {state.authenticationIssue?.recovery === 'signin' && <button className="btn btn-primary" disabled={busy} onClick={signin}>{t('Sign in again')}</button>}
    {state.authenticationIssue?.recovery === 'enroll' && !open && <button className="btn btn-primary" onClick={() => setOpen(true)}>{t('Set up your authenticator')}</button>}
    {open && !codes.length && <section className="worker-card" aria-label={t('Set up your authenticator')}>
      <MfaEnrollment enrollment={enrollment} loading={busy} error={error} verificationCode={code}
        onVerificationCodeChange={setCode} onConfirm={confirm} onStart={begin}
        onCancel={() => { if (!inFlight.current) { clear(); setOpen(false) } }}
        onExpire={() => { clear(); setExpired(true) }} expired={expired} />
      {!enrollment && <button className="btn btn-secondary" disabled={busy} onClick={() => { clear(); setOpen(false) }}>{t('Cancel')}</button>}
    </section>}
    {codes.length > 0 && <section className="worker-card" aria-label={t('Save your recovery codes')}>
      <h2>{t('Save your recovery codes')}</h2>
      <p>{t('Each code can be used once. Store them securely; they will not be shown again.')}</p>
      <pre data-testid="mfa-recovery-codes">{codes.join('\n')}</pre>
      {error && <p role="alert">{t(error)}</p>}
      <button className="btn btn-primary" disabled={busy} onClick={acknowledge}>{t('I saved these recovery codes')}</button>
    </section>}
    {!open && error && <p role="alert">{t(error)}</p>}
    {open && error && !codes.length && <button className="btn btn-secondary" disabled={busy} onClick={signin}>{t('Sign in again')}</button>}
  </>
}
